// "Continue recording" after Stop: the earlier recording is kept up to a chosen moment and the
// server export puts it in front of the new recording. Times are on the recording's own clock
// (the same clock the Auto-Pace speed chunks and the "Insert clip" timeline use).

export interface ExportChunk {
    startMs: number;
    endMs: number;
    speed: number;
    kind: 'speaking' | 'silent';
    sourceClip?: { notebookId: string; nodeId: string; videoId: string };
}

export interface ResumePart {
    /** Job id whose raw.webm holds this earlier recording (recordings/{uid}/{jobId}/raw.webm). */
    jobId: string;
    /** The earlier recording's export chunks, already cut at the resume moment. */
    chunks: ExportChunk[];
    /** Where the earlier recording was cut, on its own clock. */
    keptMs: number;
    uploaded: boolean;
}

/** Chunks of one recording kept up to cutMs. Videos inserted with "Insert clip" (sourceClip) stay
 * when they come before the cut; one placed after the last kept stretch goes with the rest. */
export function cutChunksAt(chunks: ExportChunk[], cutMs: number): ExportChunk[] {
    const out: ExportChunk[] = [];
    for (const c of chunks) {
        if (c.sourceClip) { out.push(c); continue; }
        if (c.startMs >= cutMs) break;
        const endMs = Math.min(c.endMs, cutMs);
        if (endMs - c.startMs >= 1) out.push({ ...c, endMs });
        if (c.endMs >= cutMs) break;
    }
    while (out.length && out[out.length - 1].sourceClip) out.pop();
    return out;
}

/** Export chunks for the earlier parts, each pointing at its own raw recording. */
export function resumeExportChunks(parts: ResumePart[]) {
    return parts.flatMap(p => p.chunks.map(c => (c.sourceClip ? c : { ...c, sourceRaw: { jobId: p.jobId } })));
}
